import React from 'react';
import { AIAttributeDefinition } from '../../types';

interface DynamicAIFieldsProps {
  attributeDefinitions: AIAttributeDefinition[];
  values: Record<string, any>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  additionalCategoriesSection?: React.ReactNode;
}

export const DynamicAIFields: React.FC<DynamicAIFieldsProps> = ({
  attributeDefinitions,
  values,
  onChange,
  disabled = false,
  additionalCategoriesSection
}) => {
  // Filter out special fields that are displayed in the main product form or are AI metadata
  const specialFields = [
    'product_title',
    'market_description',
    'detailed_inventory_description',
    'confidence_level'  // AI metadata, not a product attribute
  ];
  const filteredDefinitions = attributeDefinitions.filter(
    attr => !specialFields.includes(attr.attribute_key)
  );

  // Group attributes by scope for better organization
  const groupedAttributes = filteredDefinitions.reduce((acc, attr) => {
    if (!acc[attr.attribute_scope]) {
      acc[attr.attribute_scope] = [];
    }
    acc[attr.attribute_scope].push(attr);
    return acc;
  }, {} as Record<string, AIAttributeDefinition[]>);

  // Define the display order for scopes (includes both legacy and new schema-based scopes)
  const scopeOrder = [
    'square_projection',      // NEW: Schema-based customer-facing attributes
    'internal_record',        // NEW: Schema-based internal attributes
    'categories',             // LEGACY
    'custom_attributes',      // LEGACY
    'attributes_rich',        // LEGACY
    'internal_attributes'     // LEGACY
  ];

  // Scope labels for display
  const scopeLabels: Record<string, string> = {
    'square_projection': 'Product Information',  // NEW: Customer-facing data
    'internal_record': 'Internal Attributes',    // NEW: Internal inventory data
    'categories': 'Product Categories',          // LEGACY
    'custom_attributes': 'Square Custom Attributes',  // LEGACY
    'attributes_rich': 'Additional Attributes',  // LEGACY
    'internal_attributes': 'Internal Fields'     // LEGACY
  };

  // Render a single field based on its type
  const renderField = (attr: AIAttributeDefinition) => {
    const value = values[attr.attribute_key] || '';
    
    switch (attr.attribute_type) {
      case 'enum':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value}
              onChange={(e) => onChange(attr.attribute_key, e.target.value)}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              required={attr.is_required}
            >
              <option value="">Select {attr.attribute_name}</option>
              {[...(attr.possible_values || [])].sort((a, b) => a.localeCompare(b)).map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
            {attr.prompt_instruction && (
              <p className="text-xs text-gray-500 mt-1">{attr.prompt_instruction}</p>
            )}
          </div>
        );

      case 'multi_select':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              multiple
              value={value ? (Array.isArray(value) ? value : [value]) : []}
              onChange={(e) => {
                const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                onChange(attr.attribute_key, selectedOptions.join(','));
              }}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              required={attr.is_required}
              size={Math.min(5, attr.possible_values?.length || 3)}
            >
              {[...(attr.possible_values || [])].sort((a, b) => a.localeCompare(b)).map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Hold Ctrl/Cmd to select multiple. {attr.prompt_instruction}
            </p>
          </div>
        );
        
      case 'text':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(attr.attribute_key, e.target.value)}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              placeholder={`Enter ${attr.attribute_name.toLowerCase()}`}
              required={attr.is_required}
            />
            {attr.prompt_instruction && (
              <p className="text-xs text-gray-500 mt-1">{attr.prompt_instruction}</p>
            )}
          </div>
        );
        
      case 'number':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(attr.attribute_key, e.target.value)}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              placeholder={`Enter ${attr.attribute_name.toLowerCase()}`}
              required={attr.is_required}
            />
            {attr.prompt_instruction && (
              <p className="text-xs text-gray-500 mt-1">{attr.prompt_instruction}</p>
            )}
          </div>
        );

      case 'string':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => onChange(attr.attribute_key, e.target.value)}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              placeholder={`Enter ${attr.attribute_name.toLowerCase()}`}
              required={attr.is_required}
              rows={3}
            />
            {attr.prompt_instruction && (
              <p className="text-xs text-gray-500 mt-1">{attr.prompt_instruction}</p>
            )}
          </div>
        );

      case 'color':
      case 'color_or_null':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(attr.attribute_key, e.target.value)}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              placeholder={`Enter color (e.g., "Red", "Blue")`}
              required={attr.is_required}
            />
            {attr.prompt_instruction && (
              <p className="text-xs text-gray-500 mt-1">{attr.prompt_instruction}</p>
            )}
          </div>
        );

      case 'comma_separated':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attr.attribute_name}
              {attr.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(attr.attribute_key, e.target.value)}
              disabled={disabled}
              className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                disabled ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              placeholder={`Enter comma-separated values`}
              required={attr.is_required}
            />
            {attr.prompt_instruction && (
              <p className="text-xs text-gray-500 mt-1">{attr.prompt_instruction}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Sort attributes within each group by display_order
  Object.keys(groupedAttributes).forEach(scope => {
    groupedAttributes[scope].sort((a, b) => a.display_order - b.display_order);
  });

  // Get the scopes in the defined order, filtering out any that don't have attributes
  const orderedScopes = scopeOrder.filter(scope => groupedAttributes[scope] && groupedAttributes[scope].length > 0);

  return (
    <div className="space-y-6">
      {orderedScopes.map((scope, index) => {
        const attrs = groupedAttributes[scope];
        return (
          <React.Fragment key={scope}>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {scopeLabels[scope] || scope}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {scope === 'square_projection' && 'Customer-facing product information that will be synced to Square POS.'}
                {scope === 'internal_record' && 'Internal inventory data for warehouse management and tracking.'}
                {scope === 'categories' && 'Select categories that will be automatically created in Square if they don\'t exist.'}
                {scope === 'custom_attributes' && 'These attributes will be synced to Square as custom fields.'}
                {scope === 'attributes_rich' && 'Additional product details for internal tracking.'}
                {scope === 'internal_attributes' && 'Internal system fields.'}
              </p>

              {/* Enum dropdowns in a grid at the top */}
              {attrs.filter(attr => attr.attribute_type === 'enum' || attr.attribute_type === 'multi_select').length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  {attrs.filter(attr => attr.attribute_type === 'enum' || attr.attribute_type === 'multi_select').map(attr => (
                    <div key={attr.id}>
                      {renderField(attr)}
                    </div>
                  ))}
                </div>
              )}

              {/* Other short fields (color, number, text) in a grid */}
              {attrs.filter(attr =>
                attr.attribute_type !== 'enum' &&
                attr.attribute_type !== 'multi_select' &&
                attr.attribute_type !== 'string' &&
                attr.attribute_type !== 'comma_separated'
              ).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                  {attrs.filter(attr =>
                    attr.attribute_type !== 'enum' &&
                    attr.attribute_type !== 'multi_select' &&
                    attr.attribute_type !== 'string' &&
                    attr.attribute_type !== 'comma_separated'
                  ).map(attr => (
                    <div key={attr.id}>
                      {renderField(attr)}
                    </div>
                  ))}
                </div>
              )}

              {/* Full-width fields (string and comma_separated) at the bottom */}
              {attrs.filter(attr => attr.attribute_type === 'string' || attr.attribute_type === 'comma_separated').map(attr => (
                <div key={attr.id} className="mb-6">
                  {renderField(attr)}
                </div>
              ))}
            </div>
            
            {/* Insert Additional Square Categories right after the scope containing the category field */}
            {additionalCategoriesSection && attrs.some(attr => attr.attribute_key === 'category') && (
              <div className="mt-6 pt-6 border-t">
                {additionalCategoriesSection}
              </div>
            )}
          </React.Fragment>
        );
      })}
      
      {filteredDefinitions.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No AI attributes configured.</p>
          <p className="text-sm mt-2">Configure attributes in the organization settings to enable AI analysis.</p>
        </div>
      )}
    </div>
  );
};